import type { AbcClass, ConsumptionRow } from '../components/consumption/types';

export const CONSUMPTION_EXPORT_BASIS = 'Net fulfilled inventory movements';

interface ConsumptionExportContext {
  startDate: Date;
  endDate: Date;
  locationId: string | null;
  locationName?: string | null;
}

const localDate = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Escape RFC 4180 CSV cells and neutralise spreadsheet formula injection. */
export function csvCell(value: string | number | null | undefined): string {
  let text = value == null ? '' : String(value);
  if (typeof value === 'string' && /^[=+\-@\t\r\n]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}

export function buildConsumptionCsv(
  rows: ConsumptionRow[],
  abcBySku: ReadonlyMap<string, AbcClass>,
  context: ConsumptionExportContext,
): string {
  const headers = [
    'SKU',
    'Type',
    'Colour',
    'Size',
    'Category',
    'Pre-printed',
    'Net units consumed',
    'Gross units out',
    'Units returned/corrected',
    'Previous-period units',
    'Velocity per day',
    'Trend',
    'Change percent',
    'Fulfilled orders',
    'Days of cover',
    'On hand',
    'ABC class',
    'First consumed at',
    'Last consumed at',
    'Period start',
    'Period end',
    'Location scope',
    'Location ID',
    'Metric basis',
  ];

  const start = localDate(context.startDate);
  const end = localDate(context.endDate);
  const locationScope = context.locationId ? (context.locationName ?? 'Selected location') : 'All locations';
  const records = rows.map((row) => [
    row.sku,
    row.type,
    row.color,
    row.size,
    row.category,
    row.isPreprinted ? 'Yes' : 'No',
    row.units,
    row.grossUnits,
    row.returnedUnits,
    row.previousUnits,
    row.velocity,
    row.trend,
    row.deltaPct == null ? '' : (row.deltaPct * 100).toFixed(2),
    row.orders,
    row.daysOfCover ?? '',
    row.onHand,
    abcBySku.get(row.sku) ?? 'C',
    row.firstConsumedAt ?? '',
    row.lastConsumedAt ?? '',
    start,
    end,
    locationScope,
    context.locationId ?? '',
    CONSUMPTION_EXPORT_BASIS,
  ]);

  // UTF-8 BOM keeps Excel from corrupting non-ASCII product metadata.
  return `\uFEFF${[headers, ...records].map((record) => record.map(csvCell).join(',')).join('\r\n')}\r\n`;
}

export function consumptionExportFilename(startDate: Date, endDate: Date): string {
  return `inventory-consumption-${localDate(startDate)}-to-${localDate(endDate)}.csv`;
}

export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
